import { useState } from "react";

import { suspendUser, unsuspendUser } from "../../api/admin";
import {
  SUSPENSION_REASON_MAX,
  suspensionActionFor,
  suspensionReasonProblem,
} from "../../utils/accountSuspension";

/**
 * Suspends a professional's account or lifts the suspension, for the row the
 * admin picked. A suspended account is signed out on every device and cannot
 * sign in; the reason goes in the audit log and in the notice the person is
 * sent. `onDone` gets what to tell the admin once the server has done it.
 */
export function AccountSuspensionPanel({ account, onCancel, onDone }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const action = suspensionActionFor(account.status);
  if (!action) return null;
  const suspending = action === "SUSPEND";
  const name = account.fullName || account.email;

  async function submit() {
    const problem = suspending ? suspensionReasonProblem(reason) : null;
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (suspending) {
        await suspendUser(account.userId, reason.trim());
        onDone(`${name} is suspended and signed out on every device.`);
      } else {
        await unsuspendUser(account.userId);
        onDone(`${name} can sign in again.`);
      }
    } catch (err) {
      setError(err.message ?? "That didn't work. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-4 bg-slate-50 border border-slate-300 rounded-xl space-y-3">
      <h3 className="font-bold text-slate-900 text-sm sm:text-base break-words">
        {suspending ? "Suspend account" : "Reinstate account"}:{" "}
        <span className="text-slate-700">{name}</span>
      </h3>
      <p className="text-xs text-slate-600">
        {suspending
          ? "They are signed out on every device and cannot sign in until you reinstate them. The reason goes in the audit log and in the notice they are sent."
          : "They can sign in again and are sent a notice that the suspension was lifted."}
      </p>

      {suspending && (
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-label="Reason for the suspension"
          placeholder="Reason for the suspension"
          maxLength={SUSPENSION_REASON_MAX}
          rows={2}
          className="w-full text-xs sm:text-sm p-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white"
        />
      )}

      {error && (
        <p role="alert" className="text-xs font-semibold text-red-700">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 ${
            suspending
              ? "bg-red-600 hover:bg-red-700"
              : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {submitting
            ? "Saving..."
            : suspending
              ? "Suspend account"
              : "Reinstate account"}
        </button>
      </div>
    </div>
  );
}
